import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "../components/SideBar";
import { topCoinDistributionCardService } from "../services/topCoinDistributionCardService";
import TopCoinDistributionQrModal from "../components/TopCoinDistributionQrModal";
import { toast } from "react-toastify";
import { MdFileDownload, MdOutlinePictureAsPdf } from "react-icons/md";

function ViewAvailableTopCoinDistributionCards() {
  const navigate = useNavigate();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [batchRecords, setBatchRecords] = useState({});
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [selectedCardForQr, setSelectedCardForQr] = useState(null);
  const [downloadingBatch, setDownloadingBatch] = useState(null);
  const [downloadingSingleId, setDownloadingSingleId] = useState(null);

  // Filters and pagination
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await topCoinDistributionCardService.getAvailableBundles();
      if (res.status) {
        setBundles(res.data);
      }
    } catch (error) {
      console.error("Failed to load bundles:", error);
      toast.error("Failed to load available bundles");
    } finally {
      setLoading(false);
    }
  };

  const filteredBundles = bundles.filter((bundle) => {
    const prefix = (bundle.base_prefix || bundle.prefixes || "").split(",")[0].trim();
    const tiers = bundle.tiers || "";
    const searchLower = search.toLowerCase();
    return prefix.toLowerCase().includes(searchLower) || tiers.toLowerCase().includes(searchLower);
  });

  const totalPages = Math.ceil(filteredBundles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedBundles = filteredBundles.slice(startIndex, startIndex + itemsPerPage);

  const handleRowClick = async (batchTime) => {
    if (expandedRow === batchTime) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(batchTime);
    if (!batchRecords[batchTime]) {
      setLoadingRecords(true);
      try {
        const res = await topCoinDistributionCardService.getCardsByBatch(batchTime);
        if (res.status) {
          setBatchRecords((prev) => ({ ...prev, [batchTime]: res.data }));
        }
      } catch (error) {
        toast.error("Failed to load batch records");
      } finally {
        setLoadingRecords(false);
      }
    }
  };

  const downloadCSV = async (bundle, e) => {
    if (e) e.stopPropagation();
    const batchTime = bundle.batch_time;
    setDownloadingBatch(batchTime);

    let cardsToDownload = batchRecords[batchTime];
    if (!cardsToDownload) {
      try {
        const res = await topCoinDistributionCardService.getCardsByBatch(batchTime);
        if (res.status) {
          cardsToDownload = res.data;
          setBatchRecords((prev) => ({ ...prev, [batchTime]: res.data }));
        } else {
          toast.error("Failed to load cards for download");
          setDownloadingBatch(null);
          return;
        }
      } catch (error) {
        toast.error("Error downloading CSV");
        setDownloadingBatch(null);
        return;
      }
    }

    if (!cardsToDownload || cardsToDownload.length === 0) {
      toast.info("No cards found in this bundle.");
      setDownloadingBatch(null);
      return;
    }

    const headers = ["CARD NUMBER", "UNIQUE SERIAL NUMBER", "TOPCOIN VALUE", "TIER", "QR CODE"];
    const rows = cardsToDownload.map(
      (c) => `"${c.card_number}","${c.serial_number}","${c.top_coin_value}","${c.card_tier}","${c.qr_code_data}"`
    );
    const csvContent = [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const safeName = batchTime.replace(/[\s:]/g, "_");
    const prefixName = (bundle?.base_prefix || bundle?.prefixes || "batch").split(",")[0].trim();
    link.setAttribute("download", `TopCoin_Bundle_${prefixName}_${safeName}_cards.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setDownloadingBatch(null);
  };

  const handleDownloadSinglePdf = async (record) => {
    try {
      setDownloadingSingleId(record.id);
      await topCoinDistributionCardService.downloadSingleCardQrPdf(record.id, record.card_number);
      toast.success(`PDF for card ${record.card_number} downloaded!`);
    } catch (error) {
      console.error("Failed to download single card PDF:", error);
      toast.error("Failed to download card PDF");
    } finally {
      setDownloadingSingleId(null);
    }
  };

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{ border: 0, boxShadow: "none" }}>
          <div className="header-top">
            <div>
              <h2 className="header-title">View TopCoin Card Bundles & QR Codes</h2>
              <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "13px" }}>
                Inspect generated batches, preview individual card QR codes, and export CSV files.
              </p>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                className="action-btn"
                onClick={() => navigate("/manage-topcoin-distribution-cards")}
                style={{ backgroundColor: "#1e3a8a" }}
              >
                Back to Manage Cards
              </button>
            </div>
          </div>

          <div className="filters">
            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by Prefix "
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
                maxLength={50}
              />
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: "30px", color: "#64748b" }}>Loading bundles...</div>
          ) : (
            <div className="table-container">
              <table className="member-table">
                <thead>
                  <tr>
                    <th>Batch Time (Generated At)</th>
                    <th>Prefix</th>
                    <th>Tiers & Values</th>
                    <th>Start Card No</th>
                    <th>End Card No</th>
                    <th>Start Serial</th>
                    <th>End Serial</th>
                    <th>Quantity</th>
                    <th>Status</th>
                    <th style={{ textAlign: "center", minWidth: "150px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedBundles.length > 0 ? (
                    paginatedBundles.map((bundle) => (
                      <React.Fragment key={bundle.batch_time}>
                        <tr
                          onClick={() => handleRowClick(bundle.batch_time)}
                          style={{ cursor: "pointer", transition: "background-color 0.2s" }}
                          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          <td>{new Date(bundle.batch_time).toLocaleString()}</td>
                          <td>
                            <strong style={{ fontFamily: "monospace", fontSize: "13px", color: "#1e3a8a" }}>
                              {(bundle.base_prefix || bundle.prefixes || "").split(",")[0].trim()}
                            </strong>
                          </td>
                          <td>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                borderRadius: "4px",
                                fontSize: "11px",
                                fontWeight: 700,
                                backgroundColor: bundle.sample_color ? `${bundle.sample_color}20` : "#e2e8f0",
                                color: bundle.sample_color || "#334155",
                                border: `1px solid ${bundle.sample_color || "#cbd5e1"}`,
                              }}
                            >
                              {bundle.tiers} ({bundle.values_list} TC)
                            </span>
                          </td>
                          <td><code style={{ fontSize: "12px" }}>{bundle.start_card_no}</code></td>
                          <td><code style={{ fontSize: "12px" }}>{bundle.end_card_no}</code></td>
                          <td><code style={{ fontSize: "12px", color: "#475569" }}>{bundle.start_serial}</code></td>
                          <td><code style={{ fontSize: "12px", color: "#475569" }}>{bundle.end_serial}</code></td>
                          <td><strong>{bundle.quantity}</strong></td>
                          <td>
                            <span className={`status ${bundle.status || "unassigned"}`}>
                              {bundle.status || "unassigned"}
                            </span>
                          </td>
                          <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center", whiteSpace: "nowrap", padding: "10px 14px" }}>
                            <button
                              type="button"
                              onClick={(e) => downloadCSV(bundle, e)}
                              disabled={downloadingBatch === bundle.batch_time}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "6px",
                                backgroundColor: "#059669",
                                color: "#ffffff",
                                border: "none",
                                borderRadius: "6px",
                                padding: "6px 14px",
                                fontSize: "12px",
                                fontWeight: "600",
                                letterSpacing: "0.2px",
                                whiteSpace: "nowrap",
                                cursor: downloadingBatch === bundle.batch_time ? "wait" : "pointer",
                                opacity: downloadingBatch === bundle.batch_time ? 0.75 : 1,
                                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
                                transition: "all 0.15s ease-in-out",
                              }}
                              onMouseOver={(e) => {
                                if (downloadingBatch !== bundle.batch_time) {
                                  e.currentTarget.style.backgroundColor = "#047857";
                                  e.currentTarget.style.boxShadow = "0 2px 6px rgba(5, 150, 105, 0.25)";
                                }
                              }}
                              onMouseOut={(e) => {
                                if (downloadingBatch !== bundle.batch_time) {
                                  e.currentTarget.style.backgroundColor = "#059669";
                                  e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.08)";
                                }
                              }}
                            >
                              <MdFileDownload size={16} />
                              <span>{downloadingBatch === bundle.batch_time ? "Downloading..." : "Download CSV"}</span>
                            </button>
                          </td>
                        </tr>
                        {expandedRow === bundle.batch_time && (
                          <tr>
                            <td colSpan="10" style={{ backgroundColor: "#f8fafc", padding: "16px", borderBottom: "1px solid #e2e8f0" }}>
                              {loadingRecords && !batchRecords[bundle.batch_time] ? (
                                <div style={{ textAlign: "center", padding: "10px", color: "#64748b" }}>Loading cards...</div>
                              ) : (
                                <div style={{ maxHeight: "320px", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#fff" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                    <thead style={{ backgroundColor: "#f1f5f9", position: "sticky", top: 0, zIndex: 1 }}>
                                      <tr>
                                        <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Card No</th>
                                        <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Serial Number</th>
                                        <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Tier & Value</th>
                                        <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Status</th>
                                        <th style={{ padding: "8px 12px", textAlign: "center", borderBottom: "1px solid #cbd5e1" }}>QR Code</th>
                                        <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Created At</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {batchRecords[bundle.batch_time]?.length > 0 ? (
                                        batchRecords[bundle.batch_time].map((record) => (
                                          <tr key={record.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                            <td style={{ padding: "8px 12px", fontFamily: "monospace", fontWeight: 600 }}>{record.card_number}</td>
                                            <td style={{ padding: "8px 12px", fontFamily: "monospace", color: "#475569" }}>{record.serial_number}</td>
                                            <td style={{ padding: "8px 12px" }}>
                                              <span
                                                style={{
                                                  padding: "2px 6px",
                                                  borderRadius: "4px",
                                                  fontSize: "11px",
                                                  fontWeight: 700,
                                                  backgroundColor: `${record.card_color}20`,
                                                  color: record.card_color,
                                                }}
                                              >
                                                {record.card_tier} ({record.top_coin_value} TC)
                                              </span>
                                            </td>
                                            <td style={{ padding: "8px 12px" }}>
                                              <span className={`status ${record.status}`} style={{ fontSize: "11px", padding: "2px 6px" }}>
                                                {record.status}
                                              </span>
                                            </td>
                                            <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                              <div style={{ display: "inline-flex", gap: "6px", alignItems: "center", justifyContent: "center" }}>
                                                <button
                                                  type="button"
                                                  onClick={() => setSelectedCardForQr(record)}
                                                  style={{
                                                    padding: "3px 8px",
                                                    backgroundColor: "#3b82f6",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    fontSize: "11px",
                                                    cursor: "pointer",
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  View QR
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => handleDownloadSinglePdf(record)}
                                                  disabled={downloadingSingleId === record.id}
                                                  title="Download Card QR PDF"
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "3px",
                                                    padding: "3px 8px",
                                                    backgroundColor: "#0284c7",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    fontSize: "11px",
                                                    cursor: downloadingSingleId === record.id ? "wait" : "pointer",
                                                    fontWeight: 600,
                                                    opacity: downloadingSingleId === record.id ? 0.7 : 1,
                                                  }}
                                                >
                                                  <MdOutlinePictureAsPdf size={12} />
                                                  <span>{downloadingSingleId === record.id ? "..." : "PDF"}</span>
                                                </button>
                                              </div>
                                            </td>
                                            <td style={{ padding: "8px 12px", color: "#64748b" }}>
                                              {new Date(record.created_at).toLocaleString()}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td colSpan="6" style={{ padding: "10px", textAlign: "center", color: "#64748b" }}>
                                            No records found in this batch.
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" style={{ textAlign: "center", padding: "20px", color: "#64748b" }}>
                        No card bundles found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="pagination">
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedCardForQr && (
        <TopCoinDistributionQrModal
          card={selectedCardForQr}
          onClose={() => setSelectedCardForQr(null)}
        />
      )}
    </div>
  );
}

export default ViewAvailableTopCoinDistributionCards;
